import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import vscDarkPlus from 'react-syntax-highlighter/dist/esm/styles/prism/vsc-dark-plus';
// 导入更多语言
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import php from 'react-syntax-highlighter/dist/esm/languages/prism/php';
import ruby from 'react-syntax-highlighter/dist/esm/languages/prism/ruby';
import swift from 'react-syntax-highlighter/dist/esm/languages/prism/swift';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import scala from 'react-syntax-highlighter/dist/esm/languages/prism/scala';
import kotlin from 'react-syntax-highlighter/dist/esm/languages/prism/kotlin';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import html from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import { Clipboard } from '@styled-icons/bootstrap/Clipboard';
import { ClipboardCheck } from '@styled-icons/bootstrap/ClipboardCheck'
import MermaidRenderer from './MermaidRenderer'

// 注册所有导入的语言
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('php', php);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('swift', swift);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('scala', scala);
SyntaxHighlighter.registerLanguage('kotlin', kotlin);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('html', html);

const CodeBlockRenderer = ({ props, value, language }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      style={{
        position: 'relative',
        marginTop: '1em',
        marginBottom: '1em',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #2d2d2d',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: '#1e1e1e',
          borderBottom: '1px solid #2d2d2d'
        }}
      >
        <span
          style={{
            fontSize: '12px',
            color: '#858585',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {language || 'code'}
        </span>
        <button
          onClick={() => handleCopy(value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: copied ? '#4CAF50' : '#858585',
            cursor: 'pointer',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
          }}
        >
          {copied ? <ClipboardCheck size={14} /> : <Clipboard size={14} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        showLineNumbers={true}
        wrapLongLines={true}
        wrapLines={true}
        customStyle={{
          margin: 0,
          padding: '16px',
          backgroundColor: '#1e1e1e',
          fontSize: '14px',
          lineHeight: '1.6',
          borderRadius: 0,
          maxHeight: 'fit-content'
        }}
        codeTagProps={{
          style: {
            fontFamily: "'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace"
          }
        }}
        lineNumberStyle={{
          minWidth: '3em',
          paddingRight: '1em',
          color: '#858585',
          userSelect: 'none'
        }}
        PreTag="pre"
        {...props}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};

const ArtifactButton = ({ onClick, children, setArtifactOffsetTop }) => {
  const buttonRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      ref={buttonRef}
      onClick={() => {
        !!setArtifactOffsetTop && setArtifactOffsetTop(buttonRef?.current?.offsetTop);
        !!onClick && onClick();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '8px 16px',
        margin: '8px 4px',
        backgroundColor: isHovered ? '#e3f2fd' : '#f5f5f5',
        border: '1px solid',
        borderColor: isHovered ? '#2196F3' : '#ddd',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        color: isHovered ? '#1976D2' : '#555',
        transition: 'all 0.2s ease',
        boxShadow: isHovered ? '0 2px 4px rgba(33, 150, 243, 0.2)' : 'none'
      }}
    >
      {children}
    </button>
  );
};

const MarkdownRenderer = ({ content, artifacts, setArtifacts, setSelectedArtifact, setArtifactOffsetTop }) => {
  const [processedContent, setProcessedContent] = useState('');

  useEffect(() => {
    const extractArtifacts = (markdown) => {
      const artifactRegex = /<(svg)[\s\S]*?<\/\1>|```mermaid[\s\S]*?```/gi;
      const extractedArtifacts = [];
      let artifactId = 0;

      const processedMarkdown = markdown.replace(artifactRegex, (match) => {
        let type;
        if (match.startsWith('<svg')) {
          type = 'SVG';
        } else if (match.startsWith('```mermaid')) {
          type = 'Mermaid';
          match = match.replace(/```mermaid\n?/, '').replace(/```$/, '');
        }

        extractedArtifacts.push({ id: artifactId, type, content: match });
        const placeholder = `[ARTIFACT_${artifactId}]`;
        artifactId++;
        return placeholder;
      });

      setArtifacts(extractedArtifacts);
      return processedMarkdown;
    };

    const processedMarkdown = setArtifacts ? extractArtifacts(content) : content;
    setProcessedContent(processedMarkdown?.replace(/(\S)?(\*\*.*?\*\*)(\S)?/g, (match, p1, p2, p3) => {
      const before = p1 ? p1 + ' ' : '';
      const after = p3 ? ' ' + p3 : '';
      return before + p2 + after;
    }));
  }, [content]);

  const renderArtifactButton = useCallback((id) => {
    const artifact = artifacts?.find(a => a.id === id);
    if (!artifact) return null;

    return (
      <ArtifactButton
        onClick={() => {
          setSelectedArtifact(artifact)
        }}
        setArtifactOffsetTop={setArtifactOffsetTop}
      >
        View {artifact.type} Content
      </ArtifactButton>
    );
  }, [artifacts]);

  const processArtifactContent = useCallback((child) => {
    if (typeof child === 'string') {
      if (child.includes('[ARTIFACT_')) {
        const match = child.match(/\[ARTIFACT_(\d+)\]/);
        if (match) {
          const artifactId = parseInt(match[1], 10);
          const parts = child.split(/\[ARTIFACT_\d+\]/);
          return [
            parts[0],
            renderArtifactButton(artifactId),
            parts[1]
          ].filter(Boolean);
        }
      }
      return child;
    }
    if (Array.isArray(child)) {
      return child.map(processArtifactContent).flat();
    }
    if (child?.props?.children) {
      const processedChildren = processArtifactContent(child.props.children);
      return React.cloneElement(child, { children: processedChildren });
    }
    return child;
  }, [artifacts]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex, rehypeRaw]}
      components={{
        p({ node, children }) {
          const processedChildren = processArtifactContent(children);
          return <p>{processedChildren}</p>;
        },
        pre({ node, children, ...props }) {
          // 检查是否是代码块
          const codeChild = children?.props;
          if (codeChild?.className?.startsWith('language-')) {
            // 直接返回 code 内容，让 code 组件处理
            return <>{children}</>;
          }
          // 如果不是代码块，保留默认行为
          return <pre {...props}>{children}</pre>;
        },
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '')
          const value = String(children).replace(/\n$/, '');

          if (typeof children === 'string' && value.includes('[ARTIFACT_')) {
            const artifactId = parseInt(value.match(/\d+/)[0], 10);
            return renderArtifactButton(artifactId);
          }

          if (match && ['svg'].includes(match[1])) {
            return <div style={{ width: 'fit-content' }} dangerouslySetInnerHTML={{ __html: value }} />
          }

          if (match && match[1] === 'mermaid') {
            return <MermaidRenderer id={'mermaid-chart'} source={value} />
          }

          return (
            <>
              {!inline && match ? (
                <CodeBlockRenderer
                  props={props}
                  language={match && match[1]}
                  value={value}
                />
              ) : (
                <code
                  className={className}
                  style={{
                    color: '#444',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.9em',
                    fontFamily: "'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace",
                  }}
                  {...props}
                >
                  {children}
                </code>
              )}
            </>
          );
        }
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;